// TransactionApi.tsx
import axios from 'axios';
import apiConfig from '../apiConfig.ts';

const API_BASE_URL = apiConfig.apiBaseURL;

// Transaction interfaces
export interface Transaction {
  transactionId: string;
  transactionIdLast5?: string;
  transactionId8Digit?: string;
  date: string;
  customerName: string;
  customerEmail: string;
  customerId: string;
  type: string;
  paymentMethod: string;
  amountPaid: number;
  status: string;
  details: {
    subscriptionName?: string;
    subscriptionType?: string;
    orderId: string;
    promoCode?: string;
    discountPercentage?: number;
  };
}

// Refund interfaces
export interface Refund {
  _id: string;
  refundId: string;
  transactionId: string;
  amount: number;
  reason: string;
  status: string;
  createdAt: string;
  processedAt?: string;
}

// API Response interfaces
export interface TransactionResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: {
    transactions: Transaction[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalTransactions: number;
      limit: number;
    };
  };
}

export interface TransactionStatsResponse {
  status: 'Success' | 'Failed';
  message: string;
  transactionCounts?: {
    totalTransactions: number;
    successfulTransactions: number;
    failedTransactions: number;
    refundedTransactions: number;
    pendingTransactions: number;
  };
}

export interface SingleTransactionResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Transaction;
}

export interface RefundResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Refund[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface RefundStatsResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: {
    totalRefunds: number;
    pending: number;
    processed: number;
    rejected: number;
  };
}

export interface SingleRefundResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: Refund;
  errors?: string[];
}

// Parameters for API calls
export interface GetTransactionsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  type?: string;
}

export interface GetRefundsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateRefundParams {
  transactionId: string;
  refundAmount: number;
  reason: string;
  transactionType: string;
  additionalNotes?: string;
}

export interface ProcessRefundParams {
  status: 'processed' | 'rejected';
  adminNotes?: string;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const transactionApi = {
  // Get all transactions with pagination, search, and filtering
  getAllTransactions: async (params: GetTransactionsParams = {}): Promise<TransactionResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.type) queryParams.append('type', params.type);

      const response = await axios.get(
        `${API_BASE_URL}/transactions?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch transactions',
        data: {
          transactions: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalTransactions: 0,
            limit: 10,
          },
        },
      };
    }
  },

  // Get transaction statistics
  getTransactionStats: async (): Promise<TransactionStatsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/stats`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching transaction stats:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch transaction stats',
        transactionCounts: {
          totalTransactions: 0,
          successfulTransactions: 0,
          failedTransactions: 0,
          refundedTransactions: 0,
          pendingTransactions: 0
        }
      };
    }
  },

  // Get specific transaction by ID and type
  getTransactionById: async (transactionId: string, type: string): Promise<SingleTransactionResponse> => {
    try {
      console.log('Fetching transaction from URL:', `${API_BASE_URL}/transactions/${transactionId}/${type}`);

      const response = await axios.get(
        `${API_BASE_URL}/transactions/${transactionId}/${type}`,
        {
          headers: getAuthHeaders()
        }
      );

      console.log('Raw API Response:', response);
      console.log('Response data:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('Error fetching transaction:', error);
      console.error('Error response:', error.response);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch transaction'
      };
    }
  },

  // Download transaction receipt
  downloadReceipt: async (transactionId: string): Promise<Blob> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/transactions/${transactionId}/receipt`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error downloading receipt:', error);
      throw error;
    }
  },

  // Share transaction receipt via email
  shareReceipt: async (transactionId: string, emails: string[]): Promise<{ status: 'Success' | 'Failed'; message: string }> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/transactions/${transactionId}/share-receipt`,
        { emails },
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error sharing receipt:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to share receipt'
      };
    }
  },
};

export const refundApi = {
  // Get all refunds with pagination, search, and filtering
  getAllRefunds: async (params: GetRefundsParams = {}): Promise<RefundResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.status) queryParams.append('status', params.status);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axios.get(
        `${API_BASE_URL}/refunds/all-refunds?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching refunds:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch refunds',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get refund statistics
  getRefundStats: async (): Promise<RefundStatsResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/refunds/stats`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching refund stats:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch refund stats',
        data: {
          totalRefunds: 0,
          pending: 0,
          processed: 0,
          rejected: 0
        }
      };
    }
  },

  // Get specific refund by ID
  getRefundById: async (refundId: string): Promise<SingleRefundResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/refunds/${refundId}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error fetching refund:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch refund'
      };
    }
  },

  // Create a new refund request
  createRefund: async (params: CreateRefundParams): Promise<SingleRefundResponse> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/refunds/create-refund`,
        params,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error creating refund:', error);
      console.error('API Error Details:');
      console.error('- Status:', error.response?.status);
      console.error('- Response Data:', error.response?.data);
      console.error('- Validation Errors:', error.response?.data?.errors);
      console.error('- Request Data:', error.config?.data);

      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to create refund',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // Process a refund
  processRefund: async (refundId: string, params: ProcessRefundParams): Promise<SingleRefundResponse> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/refunds/${refundId}/process`,
        params,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error processing refund:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to process refund'
      };
    }
  },
};